/**
 * Customer Success Health Section
 * Section 7: Expansion alerts, at-risk accounts, time-to-value
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface CustomerSuccessHealthProps {
  data?: {
    expansionPotentialAlerts?: number;
    atRiskAccounts?: number;
    workflowReliabilityPatterns?: Array<{
      userId: string;
      reliabilityScore: number;
      pattern: string;
    }>;
    timeToValueMetrics?: {
      average: number;
      byIcp: Array<{ icp: string; average: number }>;
    };
  };
}

export function CustomerSuccessHealth({ data }: CustomerSuccessHealthProps) {
  const formatDays = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${value.toFixed(1)} days`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Expansion Potential Alerts</CardTitle>
            <CardDescription>Customers ready for upsell</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data?.expansionPotentialAlerts || 0}
            </div>
            <Button variant="outline" size="sm" className="mt-2">
              View Opportunities
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">At-Risk Accounts</CardTitle>
            <CardDescription>Customers with high churn risk</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{data?.atRiskAccounts || 0}</div>
            <Button variant="outline" size="sm" className="mt-2">
              View At-Risk
            </Button>
          </CardContent>
        </Card>
      </div>

      {data?.timeToValueMetrics && (
        <Card>
          <CardHeader>
            <CardTitle>Time to Value Metrics</CardTitle>
            <CardDescription>Average time for customers to achieve value</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="text-sm text-muted-foreground">Overall Average</div>
                <div className="text-2xl font-bold">{formatDays(data.timeToValueMetrics.average)}</div>
              </div>
              {data.timeToValueMetrics.byIcp && data.timeToValueMetrics.byIcp.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium">By ICP Segment</div>
                  {data.timeToValueMetrics.byIcp.map((icp, index) => (
                    <div key={index} className="flex justify-between items-center p-2 border rounded">
                      <span className="text-sm">{icp.icp}</span>
                      <span className="font-semibold">{formatDays(icp.average)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {data?.workflowReliabilityPatterns && data.workflowReliabilityPatterns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Workflow Reliability Patterns</CardTitle>
            <CardDescription>Reliability scores by customer</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.workflowReliabilityPatterns.map((pattern, index) => (
                <div key={index} className="flex justify-between items-center p-2 border rounded">
                  <div>
                    <span className="text-sm font-medium">User {pattern.userId.slice(0, 8)}</span>
                    <div className="text-xs text-muted-foreground">{pattern.pattern}</div>
                  </div>
                  <Badge variant={pattern.reliabilityScore >= 80 ? 'default' : 'destructive'}>
                    {pattern.reliabilityScore}/100
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
